import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
    toJS,
} from "mobx";
import Question from "models/Question";
import { AsyncTaskStatus, BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import { LLMClassifiersStore } from "./LLMClassifiersStore";
import LLMClassifierService from "services/LLMClassifierService";
import { sentimentToColor } from "components/Evaluation/Utils";
import { ModuleService } from "services/ModuleService";
import type {
    CreateLLMClassifierRequest,
    LLMCLassifierResponse,
    LLMExecutorStep,
    UpdateLLMClassifierRequest,
} from "models/LLMs";
import {
    ClassifierAccessType,
    ModelVisibility,
} from "models/LLMs/ModelVisibility";
import type { Role } from "components/Admin/Organizations/types/Role.type";
import { AuthStore } from "stores/AuthStore";
import {
    MlModelForLlmExecution,
    ModelComputeFrequency,
    ModelComputeOption,
    ModelComputeQuota,
    ModelOption,
} from "models/ModelCompute";
import { v4 } from "uuid";
import { LLMClassifierImplementationConfigurationType } from "models/LLMs/LLMClassifierImplementation";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import { Tag } from "models/Tag";
import { AnswerColor } from "components/UI/AcxAnswerSelector";
import type { TestLLMClassifierAgreementResponse } from "models/LLMs/Testing/TestLLMClassifierAgreementResponse";
import type { TestLLMClassifierOnConversationsResponse } from "models/LLMs/Testing/TestLLMClassifierOnConversationsResponse";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import type { DataLabelingModule } from "models/LLMs/DataLabelingModule";
import { LLMClassifierPurpose } from "models/LLMs/LLMClassifierPurpose";
import type PublishedModelResultsReconcileResult from "models/PublishedModelResultsReconcileResult";
import { LLMClassifier } from "../DataClasses/LLMClassifier";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import TestLLMClassifierService from "services/TestLLMClassifierService";
import SyntheticDataService from "services/SyntheticDataService";
import type { HubConnection } from "@microsoft/signalr";
import type {
    SyntheticDataBatch,
    SyntheticDataBatchRequest,
    SyntheticDataBatchStatus,
} from "services/SyntheticDataService";
import {
    getBatchTotals,
    getFailureHeadline,
    hasGeneratingBatches,
    isGenerating,
} from "../Components/SyntheticData/syntheticDataBatchProgress";
import type {
    ClassifierTestProgress,
    TestSetLabelCount,
} from "services/TestLLMClassifierService";
import { ModelComputeService } from "services/ModelComputeService";
import {
    LLM_MODELS_VARIABLE_GROUP_LABEL,
    getLLMVariableNames,
    getPromptVariableReferences,
    isModelVariable,
    LLMVariable,
    LLMVariableCategory,
    LLMVariableGroup,
    LLMVariableType,
    matchesLLMVariableName,
    SlimLLMVariable,
} from "models/LLMs/LLMVariable";
import ClassifierService from "services/ClassifierService";
import LLMClassifierLibraryService from "services/LLMClassifierLibraryService";
import { ExtendedMetadataClassifier } from "models/ClassifierModel";
import { ServiceError } from "services/BaseService";
import { UserService } from "services/UserService";
import {
    cloneLLMClassifierSaveSnapshot,
    getLLMClassifierSaveChanges,
} from "../utils/llmClassifierSaveChanges";
import type { LLMClassifierSaveChanges } from "../utils/llmClassifierSaveChanges";
import { defaultComparisonVersions } from "../utils/llmClassifierVersionDisplay";
import type { ModelComputeParentRequest } from "models/LLMs/ModelChain";
import { Condition } from "models/LLMs/LogicChaining/Conditions/Condition";
import type { ICondition } from "models/LLMs/LogicChaining/Conditions/Condition";
import {
    type LLMClassifierApprovalEventSummary,
    type LLMClassifierApprovalReviewer,
    type RecordLLMClassifierApprovalEventRequest,
} from "models/LLMs/LLMClassifierApproval";
import { ConditionPrimitive } from "models/LLMs/LogicChaining/Conditions/ConditionPrimitive";
import { ConditionGroup } from "models/LLMs/LogicChaining/Conditions/ConditionGroup";
import { deepPascalCase } from "../utils";
import { approvalNotificationToastMessages } from "../utils/llmClassifierApproval";
import { isEqual } from "lodash";

/** Mirrors the loaded version in the URL so it survives a refresh and can be linked. */
export const LOADED_VERSION_SEARCH_PARAM = "version";

// Replaces rather than pushes, as the stepper does: back/forward would bypass the unsaved-changes prompt.
function writeLoadedVersionToUrl(implementationId: string) {
    const url = new URL(window.location.href);
    url.searchParams.set(LOADED_VERSION_SEARCH_PARAM, implementationId);
    window.history.replaceState(null, "", url);
}

/**
 * A condition after `deepPascalCase`, as it sits on the wire inside a step.
 */
interface SerializedCondition {
    PropertyName?: string;
    ClassifierId?: string;
    SubRules?: SerializedCondition[];
}

interface ModelComputeRelationsEditor {
    getParentModelsForSave(
        childClassifierId: string,
    ): Promise<ModelComputeParentRequest[]>;
    getParentModelNamesForDiff(): { before: string[]; after: string[] };
    getLLMParentModelNames(): string[];
    whenChainSettled(): Promise<void>;
}

export enum LLMClassifierDefinedOutputsType {
    FromQuestion,
    UserDefined,
}

export enum TestingMode {
    Agreement,
    OnConversations,
}

/** One answer tag of the Data step question and what it means. */
export interface DataStepRubricItem {
    id: string;
    result: string;
    definition: string;
}

/**
 * Facilitates management of a single llm classifier for creation and editing purposes.
 */
@AcxStore
export class LLMClassifierBuilderStore extends BaseStore {
    public static Tasks = {
        SaveLLMClassifier: "Save LLM Classifier",
        TestLLMClassifier: "Test LLM Classifier",
        LabeledConversationCounts: "Labeled Conversation Counts",
        AgreementLabelCounts: "Agreement Label Counts",
        DataStepLabelCounts: "Data Step Label Counts",
        RequestSyntheticDataBatches: "Request Synthetic Data Batches",
        ClearSyntheticDataBatches: "Clear Synthetic Data Batches",
        LoadExtendedMetadata: "Load Extended Metadata",
        LoadLLMClassifierVersions: "Load LLM Classifier Versions",
        LoadEditLLMClassifier: "Load Edit LLM Classifier",
        LoadModelComputeOptions: "Load Model Compute Options",
        LoadVariables: "Load Variables",
        UpdateFromLibrary: "Update From Library",
        LoadApprovalReviewers: "Load Approval Reviewers",
        RecordApprovalEvent: "Record Approval Event",
        LoadChangeRecord: (implementationId: string) =>
            `Load Change Record ${implementationId}`,
    } as const;

    /**
     * Minimum (inclusive) number of conversations that need to be labeled for
     * each tag before we can test.
     */
    public static readonly MinimumLabeledConversationsCount: number = 5;
    /**
     * Minimum (inclusive) number of conversations considered optimal for testing.
     * This number does not guard the ability to test or save the model in any way.
     */
    public static readonly OptimalLabeledConversationsCount: number = 25;

    /**
     * Maximum number of conversations to test against per tag.
     */
    public static readonly MaxModelTestSampleSize: number = 500;

    public static readonly TestingPerSectionPageSize: number = 10;

    /** Silence after which a generating batch reconnects and the list reloads, as classifier tests do. */
    public static readonly SyntheticDataBatchSilenceMs: number = 30_000;

    private readonly messageStore: MessageStore;
    private readonly llmClassifiersStore: LLMClassifiersStore;
    public readonly authStore: AuthStore;

    private readonly llmClassifierService: LLMClassifierService;
    private readonly llmClassifierLibraryService: LLMClassifierLibraryService;
    private readonly testLlmClassifierService: TestLLMClassifierService;
    private readonly syntheticDataService = new SyntheticDataService();
    private readonly moduleService: ModuleService;
    private readonly modelComputeService: ModelComputeService;
    private readonly classifierService: ClassifierService;
    private readonly userService: UserService;
    private modelComputeRelationsEditor?: ModelComputeRelationsEditor;

    private readonly applicationFilterValuesStore: ApplicationFilterValuesStore;

    private testLLMClassifierAbortController?: AbortController;
    @observable public testProgress?: ClassifierTestProgress;

    @observable public classifier: LLMClassifier;
    @observable public llmClassifierVersions: LLMClassifierImplementation[];
    @observable public comparisonBeforeVersionId?: string;
    @observable public comparisonAfterVersionId?: string;
    @observable public approvalReviewers: LLMClassifierApprovalReviewer[] = [];
    @observable public savedClassifierBaseline?: LLMCLassifierResponse;
    @observable public loadedVersionParents: {
        classifierId: string;
        name?: string;
    }[] = [];

    @observable public modelComputeOption: ModelComputeOption;
    @observable public mlModelInformation: MlModelForLlmExecution[] = [];

    @observable public labeledConversationCounts?: LabeledConversationsCount[];
    // Human labels an agreement test can use, and the question they were counted for.
    @observable private agreementLabelCounts?: TestSetLabelCount[];
    @observable private agreementLabelCountsQuestionId?: string;
    private agreementLabelCountsAbortController?: AbortController;

    // Live tests replace these with a new response per result. Deep observability would re-copy every
    // accumulated result on each update, which stalls the UI on large or fast tests.
    @observable.ref
    public testAgreementResponse?: TestLLMClassifierAgreementResponse;
    @observable.ref
    public testOnConversationsResponse?: TestLLMClassifierOnConversationsResponse;
    @observable public isTestWithoutLabels: boolean = false;

    @observable public selectedExportKeys: Set<string> = new Set();

    @observable
    public testOnConversationsFilters: ApplicationFiltersInstanceStore;

    @observable public isSaved: boolean;
    @observable public isEditing: boolean = false;

    @observable public visibility: ModelVisibility = ModelVisibility.Anyone;
    @observable public selectedRoleIds: string[] = [];
    @observable public availableRoles: Role[] = [];

    /**
     * Used for "auto complete" in prompts via @ trigger
     */
    @observable public extendedMetadata: ExtendedMetadataClassifier[] = [];
    @observable public objectExtendedMetadata: ExtendedMetadataClassifier[] =
        [];
    @computed get allExtendedMetadata(): ExtendedMetadataClassifier[] {
        return [...this.extendedMetadata, ...this.objectExtendedMetadata];
    }

    // State for tracking selections made based on purpose, some values may or may not be present
    @observable public dataLabelingModule?: DataLabelingModule;
    @observable public dataLabelingQuestion?: Question;
    @observable public definedOutputsType?: LLMClassifierDefinedOutputsType;

    // Data step state, for the question linked on Details.
    // Not saved yet; rebuilt with blank definitions when the question changes.
    @observable public dataStepRubric: DataStepRubricItem[] = [];
    @observable public dataStepFilters: ApplicationFiltersInstanceStore;
    @observable public dataStepLabelCounts?: TestSetLabelCount[];
    @observable public dataStepSyntheticCallsPerLabel: number = 3;
    @observable public dataStepGenerateMode: "callsPerLabel" | "perOutcome" =
        "callsPerLabel";
    @observable public dataStepBalanceToTotal: boolean = true;
    // Left blank until the user picks a total.
    @observable public dataStepBalanceTarget?: number;
    // Synthetic calls typed in for each result when not balancing.
    @observable public dataStepSyntheticCallsByResult = new Map<
        string,
        number
    >();
    // This model's synthetic data batches, newest first. Undefined until the first load.
    @observable.ref public syntheticDataBatches?: SyntheticDataBatch[];
    private syntheticDataBatchesClassifierId?: string;
    private syntheticDataBatchesTimer?: ReturnType<typeof setTimeout>;
    // Bumped by every load, so a slow response for an earlier load or model is dropped.
    private syntheticDataBatchesLoad = 0;
    private isWatchingSyntheticDataBatches = false;
    // Batches seen generating while watching, so the finish notice can report on them.
    private syntheticDataBatchesSeenGenerating = new Set<string>();
    // One live connection per generating batch, since the worker's updates don't say which batch they're for.
    private syntheticDataBatchConnections = new Map<
        string,
        { connection: HubConnection; lastMessageAt: number; refresh: () => Promise<void> }
    >();
    private syntheticDataBatchesWatchdog?: ReturnType<typeof setInterval>;
    private dataStepLabelCountsAbortController?: AbortController;
    // The question and filters the loaded counts (or the last failed load) were for.
    @observable private dataStepLabelCountsKey?: string;
    @observable private dataStepLabelCountsErrorKey?: string;

    /**
     * Identifies the question and Test set filters the counts should match.
     */
    @computed get dataStepLabelCountsRequestKey(): string {
        return JSON.stringify([
            this.dataLabelingQuestion?.id,
            this.dataStepFilters.toRequestObject(),
        ]);
    }

    @computed get dataStepLabelCountsStatus(): "loading" | "loaded" | "error" {
        const key = this.dataStepLabelCountsRequestKey;
        if (this.dataStepLabelCounts && this.dataStepLabelCountsKey === key)
            return "loaded";
        if (this.dataStepLabelCountsErrorKey === key) return "error";
        return "loading";
    }

    /** Whether the rubric differs from the loaded version's. */
    @computed get hasUnsavedRubricChanges() {
        return !isEqual(
            this.savedClassifierBaseline?.classifierImplementation.rubric,
            this.classifier.implementation.rubric,
        );
    }

    /**
     * Conversations an agreement test can use for each result: every human label for the question,
     * then synthetic calls for it. Undefined until the labeled counts load.
     */
    @computed get agreementTestCountsByResult():
        | Map<string, { human: number; synthetic: number }>
        | undefined {
        const humanCounts = this.agreementLabelCounts;
        if (
            !humanCounts ||
            this.agreementLabelCountsQuestionId !== this.dataLabelingQuestion?.id
        )
            return undefined;

        const counts = new Map<string, { human: number; synthetic: number }>();
        const countsFor = (result: string) => {
            let resultCounts = counts.get(result);
            if (!resultCounts) {
                resultCounts = { human: 0, synthetic: 0 };
                counts.set(result, resultCounts);
            }
            return resultCounts;
        };
        for (const { value, count } of humanCounts)
            countsFor(value).human += count;
        // Matches the test: batches from before rubrics have no question, so they always count.
        const questionId = this.dataLabelingQuestion?.id;
        for (const batch of this.syntheticDataBatches ?? []) {
            if (batch.questionId !== null && batch.questionId !== questionId)
                continue;
            countsFor(batch.labelValue).synthetic += batch.generatedCount;
        }
        return counts;
    }

    /** The largest per-result sample size worth asking for, capped at the test limit. */
    @computed get testSampleSizeLimit(): number {
        const counts = this.agreementTestCountsByResult;
        if (!this.isAgreementTesting() || !counts)
            return this.maxModelTestSampleSizeForUser;

        const largest = Math.max(
            0,
            ...[...counts.values()].map(
                ({ human, synthetic }) => human + synthetic,
            ),
        );
        return Math.min(largest, this.maxModelTestSampleSizeForUser);
    }

    /** How many conversations "Test all" runs. Undefined when that isn't known yet. */
    @computed get testAllCount(): number | undefined {
        const counts = this.agreementTestCountsByResult;
        if (!this.isAgreementTesting() || !counts) return undefined;

        return [...counts.values()].reduce(
            (total, { human, synthetic }) =>
                total +
                Math.min(human + synthetic, this.maxModelTestSampleSizeForUser),
            0,
        );
    }

    /** Reloads the Test set counts whenever the question or a Test set filter changes. */
    public keepDataStepLabelCountsCurrent() {
        return reaction(
            () => this.dataStepLabelCountsRequestKey,
            () => void this.loadDataStepLabelCounts(),
            { fireImmediately: true, delay: 300 },
        );
    }

    @observable private flatUnmappedVariables: LLMVariable[] = [];
    @observable public variableGroups: LLMVariableGroup[] = [];

    public maxModelTestSampleSizeForUser: number;

    private variablesLoad: Promise<unknown>;

    /**
     * Resolves once the platform's variables have loaded. `refreshVariables`
     * replaces it, so awaiting it always waits on the newest load.
     */
    public get variablesLoaded(): Promise<unknown> {
        return this.variablesLoad;
    }

    /**
     * Refetches the platform's variables, which name every model and list which
     * ones are published. Stale ones hide a model published since from the
     * parents that can be picked, and show a renamed one under its old name.
     */
    public refreshVariables(): Promise<unknown> {
        return (this.variablesLoad = this.loadVariables());
    }

    constructor(private rootStore: IRootStore) {
        super("LLM Classifier Builder");
        makeObservable(this);

        this.messageStore = this.rootStore.getStore(MessageStore);
        this.llmClassifiersStore = this.rootStore.getStore(LLMClassifiersStore);
        this.authStore = this.rootStore.getStore(AuthStore);
        this.applicationFilterValuesStore = this.rootStore.getStore(
            ApplicationFilterValuesStore,
        );

        this.llmClassifierService = new LLMClassifierService();
        this.llmClassifierLibraryService = new LLMClassifierLibraryService();
        this.testLlmClassifierService = new TestLLMClassifierService();
        this.moduleService = new ModuleService();
        this.modelComputeService = new ModelComputeService();
        this.classifierService = new ClassifierService();
        this.userService = new UserService();

        this.maxModelTestSampleSizeForUser =
            LLMClassifierBuilderStore.MaxModelTestSampleSize;

        this.classifier = new LLMClassifier();

        this.loadExtendedMetadataFields();
        this.loadModelComputeOptions();
        this.variablesLoad = this.loadVariables();

        this.reset();
    }

    /**
     * Registers the chain editor without making the builder and chain stores
     * import each other. Its parent list is included in the classifier request
     * so the backend can save both in one transaction.
     */
    public registerModelComputeRelationsEditor(
        editor: ModelComputeRelationsEditor,
    ) {
        this.modelComputeRelationsEditor = editor;
    }

    @computed get nonExtendedMetadataVariables(): SlimLLMVariable[] {
        return this.variableGroups
            .filter((g) => g.label !== "Extended Metadata")
            .flatMap((g) => g.variables);
    }

    @computed get stepVariableOptions(): LLMVariableGroup[] {
        const options = this.classifier.implementation.logicChainBuilder.steps
            .map((step) => {
                const variables =
                    step.configuration.getPotentialOutputVariables();
                if (!variables || variables.length === 0) return undefined;

                return {
                    label: step.name,
                    variables: variables.map((v) => ({
                        label: v,
                        name: step.getPropertyName(v),
                        type: LLMVariableType.String,
                    })) as SlimLLMVariable[],
                };
            })
            .filter((x) => !!x);

        return options as LLMVariableGroup[];
    }

    /**
     * Resets the `llmClassifier` field to an empty object
     */
    @action
    public reset(preserveTest = false, preserveDataStep = false) {
        if (!preserveTest) {
            this.cancelTestLLMClassifier();
            this.testLLMClassifierAbortController = undefined;
            this.resetTestResults();
            this.testOnConversationsFilters = new ApplicationFiltersInstanceStore(this.rootStore);
            this.isTestWithoutLabels = false;
        }
        if (!preserveDataStep) {
            this.dataLabelingModule = undefined;
            this.dataLabelingQuestion = undefined;
            this.dataStepRubric = [];
            this.dataStepFilters = new ApplicationFiltersInstanceStore(this.rootStore);
            this.dataStepLabelCountsAbortController?.abort();
            this.dataStepLabelCounts = undefined;
            this.dataStepLabelCountsKey = undefined;
            this.dataStepLabelCountsErrorKey = undefined;
            this.dataStepSyntheticCallsPerLabel = 3;
            this.dataStepGenerateMode = "callsPerLabel";
            this.dataStepBalanceToTotal = true;
            this.dataStepBalanceTarget = undefined;
            this.dataStepSyntheticCallsByResult = new Map();
        }
        this.definedOutputsType = undefined;

        this.classifier = new LLMClassifier();
        this.llmClassifierVersions = [];
        this.clearComparisonVersions();
        this.approvalReviewers = [];
        this.savedClassifierBaseline = undefined;
        this.loadedVersionParents = [];

        this.modelComputeOption = {
            id: v4(),
            modelOption: ModelOption.LuceneOrLLM,
            modelComputeQuotas: [],
            modelComputeStructureMembers: null,
        };

        this.labeledConversationCounts = undefined;
        this.agreementLabelCountsAbortController?.abort();
        this.agreementLabelCounts = undefined;
        this.agreementLabelCountsQuestionId = undefined;
        this.isSaved = false;
        this.isEditing = false;
        this.visibility = ModelVisibility.Anyone;
        this.selectedRoleIds = [];
        this.selectedExportKeys = new Set();
    }

    @action
    private resetTestResults() {
        this.testProgress = undefined;
        this.testAgreementResponse = undefined;
        this.testOnConversationsResponse = undefined;
    }

    @action
    public setSelectedExportKeys(keys: Set<string>) {
        this.selectedExportKeys = keys;
    }

    @action
    public async prepareEditing(llmClassifier: LLMCLassifierResponse) {
        const [isEditable] =
            LLMClassifierBuilderStore.isLLMClassifierEditable(llmClassifier);
        if (!isEditable) return;

        // Saving or loading a version of this model must not interrupt its in-flight test.
        const isSameModel = this.classifier.id === llmClassifier.id;
        this.reset(
            isSameModel && !!this.testLLMClassifierAbortController && !this.testLLMClassifierAbortController.signal.aborted,
            // The linked question isn't saved yet, so keep it and the Data step across saves.
            isSameModel,
        );

        this.classifier = new LLMClassifier(llmClassifier);
        // The kept Data step shows the loaded version's rubric, not the previous version's.
        if (isSameModel) this.buildDataStepRubric(this.dataLabelingQuestion);
        this.loadedVersionParents = llmClassifier.parentModels ?? [];
        await this.variablesLoaded;

        if (llmClassifier.modelComputeOption)
            this.modelComputeOption = llmClassifier.modelComputeOption;

        this.isSaved = true;
        this.isEditing = true;

        // Derive visibility from classifier data
        if (this.classifier.isInternal) {
            this.visibility = ModelVisibility.AuthenticxOnly;
        } else if (this.classifier.rolesAccessControls.length > 0) {
            this.visibility = ModelVisibility.SpecificRoles;
            this.selectedRoleIds = this.classifier.rolesAccessControls.map(
                (r) => r.roleId,
            );
            await this.loadRoles();
        } else {
            this.visibility = ModelVisibility.Anyone;
        }

        // Every version load funnels through here, so the URL is written once rather than at each caller.
        writeLoadedVersionToUrl(llmClassifier.loadedImplementationId);

        // The chain load started above rebuilds parents and conditions; snapshotting first would bake a
        // half-hydrated baseline and report changes the user never made.
        await this.modelComputeRelationsEditor?.whenChainSettled();
        this.captureSaveBaseline();
    }

    @computed get testingMode(): TestingMode {
        if (
            this.isTestWithoutLabels ||
            this.definedOutputsType ===
                LLMClassifierDefinedOutputsType.UserDefined
        )
            return TestingMode.OnConversations;

        // Extract Data models have no fixed results to compare labels with.
        if (
            this.classifier.implementation.llmPurpose ===
                LLMClassifierPurpose.Quality ||
            (!!this.dataLabelingModule &&
                !!this.dataLabelingQuestion &&
                this.classifier.implementation.llmPurpose !==
                    LLMClassifierPurpose.ExtractData)
        )
            return TestingMode.Agreement;

        return TestingMode.OnConversations;
    }

    public isAgreementTesting() {
        return this.testingMode === TestingMode.Agreement;
    }

    public isOnConversationsTesting() {
        return this.testingMode === TestingMode.OnConversations;
    }

    @action
    public toggleTestWithoutLabels() {
        this.isTestWithoutLabels = !this.isTestWithoutLabels;
    }

    /** Takes an answer tag id. The definition is saved with the next version. */
    @action
    public setDataStepRubricDefinition(id: string, definition: string) {
        this.dataStepRubric = this.dataStepRubric.map((item) =>
            item.id === id ? { ...item, definition } : item,
        );

        const question = this.dataLabelingQuestion;
        if (!question) return;
        const taxonomy = Object.fromEntries(
            this.dataStepRubric
                .map((item) => [item.id, item.definition.trim()])
                .filter(([, definition]) => definition),
        );
        const implementation = this.classifier.implementation;
        if (Object.keys(taxonomy).length > 0) {
            implementation.setRubric({ questionId: question.id, taxonomy });
        } else if (implementation.rubric?.questionId === question.id) {
            implementation.setRubric(undefined);
        }
    }

    @action
    public setDataStepSyntheticCallsPerLabel(callsPerLabel: number) {
        this.dataStepSyntheticCallsPerLabel = callsPerLabel;
    }

    @action
    public setDataStepGenerateMode(mode: "callsPerLabel" | "perOutcome") {
        this.dataStepGenerateMode = mode;
    }

    @action
    public setDataStepBalanceTarget(target?: number) {
        this.dataStepBalanceTarget = target;
    }

    /**
     * Turns balancing on or off. Turning it off keeps each result's balanced number
     * (`currentRows`) as its starting point.
     */
    @action
    public setDataStepBalanceToTotal(
        balanceToTotal: boolean,
        currentRows: { result: string; syntheticCalls: number }[],
    ) {
        if (!balanceToTotal && this.dataStepBalanceToTotal) {
            this.dataStepSyntheticCallsByResult = new Map(
                currentRows.map((row) => [row.result, row.syntheticCalls]),
            );
        }
        this.dataStepBalanceToTotal = balanceToTotal;
    }

    /** Typing a number for one result stops balancing the rest. */
    @action
    public setDataStepSyntheticCalls(
        result: string,
        syntheticCalls: number,
        currentRows: { result: string; syntheticCalls: number }[],
    ) {
        this.setDataStepBalanceToTotal(false, currentRows);
        this.dataStepSyntheticCallsByResult.set(result, syntheticCalls);
    }

    /**
     * Sends each synthetic data batch request and returns the ones that failed, so they can be retried.
     */
    public async requestSyntheticDataBatches(
        requests: SyntheticDataBatchRequest[],
        rubricId: string,
        tagIds: Map<string, string>,
    ): Promise<SyntheticDataBatchRequest[]> {
        const results = await this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.RequestSyntheticDataBatches,
            () =>
                Promise.allSettled(
                    requests.map((request) =>
                        this.syntheticDataService.requestBatch(
                            request,
                            this.classifier.id,
                            rubricId,
                            tagIds.get(request.labelValue),
                        ),
                    ),
                ),
        );
        if (!Array.isArray(results)) return requests;
        // New batches show right away, and a cleared set goes away even if none started.
        void this.loadSyntheticDataBatches();
        return requests.filter(
            (_, index) => results[index].status === "rejected",
        );
    }

    /** Marks the model's current batches inactive so a new set replaces them. Returns whether it worked. */
    public async clearSyntheticDataBatches() {
        const result = await this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.ClearSyntheticDataBatches,
            () => this.syntheticDataService.clearBatches(this.classifier.id),
        );
        return result !== AsyncTaskStatus.Error;
    }

    /**
     * Loads this model's synthetic data batches and keeps them current with live updates while any is
     * generating. Pass whether the user can see the Data step. Returns a function that stops watching.
     */
    public watchSyntheticDataBatches(canSeeDataStep: () => boolean): () => void {
        this.isWatchingSyntheticDataBatches = true;
        const stopReaction = reaction(
            // Batches belong to a saved model, and only users who can see the Data step watch them.
            () =>
                this.isSaved && canSeeDataStep() ? this.classifier.id : undefined,
            (classifierId) => {
                // Unsaved or Data step hidden: stop updates but keep the list, since a save briefly unsets isSaved.
                if (!classifierId) {
                    clearTimeout(this.syntheticDataBatchesTimer);
                    this.syntheticDataBatchesLoad++;
                    this.closeSyntheticDataBatchConnections();
                    return;
                }
                if (classifierId !== this.syntheticDataBatchesClassifierId) {
                    runInAction(() => (this.syntheticDataBatches = undefined));
                    this.syntheticDataBatchesSeenGenerating.clear();
                    this.closeSyntheticDataBatchConnections();
                }
                void this.loadSyntheticDataBatches();
            },
            { fireImmediately: true },
        );
        return () => {
            stopReaction();
            this.isWatchingSyntheticDataBatches = false;
            clearTimeout(this.syntheticDataBatchesTimer);
            this.syntheticDataBatchesLoad++;
            this.closeSyntheticDataBatchConnections();
        };
    }

    public async loadSyntheticDataBatches() {
        clearTimeout(this.syntheticDataBatchesTimer);
        const load = ++this.syntheticDataBatchesLoad;
        const classifierId = this.isSaved ? this.classifier.id : undefined;
        if (!classifierId) return;

        let batches: SyntheticDataBatch[] | undefined;
        try {
            batches = await this.syntheticDataService.getBatches(classifierId);
        } catch (error) {
            if (load !== this.syntheticDataBatchesLoad) return;
            // A failed reload keeps the last list, and the watchdog retries while a batch is generating.
            // A failed first load shows an empty list, so Generate is still offered.
            if (!this.syntheticDataBatches) {
                if (error instanceof Error)
                    this.messageStore.logError(
                        "Failed to load synthetic data: " + error.message,
                    );
                batches = [];
            }
        }
        if (load !== this.syntheticDataBatchesLoad) return;
        if (batches) this.applySyntheticDataBatches(classifierId, batches);
    }

    /** Reloads the list shortly; calls close together become one request. */
    private scheduleSyntheticDataBatchesLoad() {
        clearTimeout(this.syntheticDataBatchesTimer);
        this.syntheticDataBatchesTimer = setTimeout(
            () => void this.loadSyntheticDataBatches(),
            500,
        );
    }

    /** Shows a new list, from a reload or a live update, and announces when the last batch finishes. */
    private applySyntheticDataBatches(
        classifierId: string,
        batches: SyntheticDataBatch[],
    ) {
        const previous =
            classifierId === this.syntheticDataBatchesClassifierId
                ? this.syntheticDataBatches
                : undefined;
        runInAction(() => {
            this.syntheticDataBatches = batches;
            this.syntheticDataBatchesClassifierId = classifierId;
        });
        for (const batch of batches.filter(isGenerating))
            this.syntheticDataBatchesSeenGenerating.add(batch.batchId);
        if (
            previous &&
            hasGeneratingBatches(previous) &&
            !hasGeneratingBatches(batches)
        )
            this.announceSyntheticDataFinished(batches);
        this.syncSyntheticDataBatchConnections(batches);
    }

    /** Applies a live update to one batch of the current model. */
    private updateSyntheticDataBatch(
        batchId: string,
        update: (batch: SyntheticDataBatch) => SyntheticDataBatch,
    ) {
        const classifierId = this.syntheticDataBatchesClassifierId;
        const batches = this.syntheticDataBatches;
        if (!classifierId || !batches?.some((batch) => batch.batchId === batchId))
            return;
        this.applySyntheticDataBatches(
            classifierId,
            batches.map((batch) =>
                batch.batchId === batchId ? update(batch) : batch,
            ),
        );
    }

    /** Keeps one live connection open for each generating batch and closes the rest. */
    private syncSyntheticDataBatchConnections(batches: SyntheticDataBatch[]) {
        const generating = new Set(
            batches.filter(isGenerating).map((batch) => batch.batchId),
        );
        for (const batchId of this.syntheticDataBatchConnections.keys())
            if (!generating.has(batchId))
                this.closeSyntheticDataBatchConnection(batchId);
        if (!this.isWatchingSyntheticDataBatches) return;
        for (const batchId of generating)
            if (!this.syntheticDataBatchConnections.has(batchId))
                this.connectToSyntheticDataBatch(batchId);
    }

    private connectToSyntheticDataBatch(batchId: string) {
        const { connection, subscribe } =
            this.syntheticDataService.connectToBatch(batchId);

        // Same recovery as classifier tests: start if needed, subscribe again, then reload,
        // because updates sent while away aren't replayed.
        let isRefreshing = false;
        const refresh = async () => {
            if (isRefreshing) return;
            isRefreshing = true;
            try {
                await subscribe();
            } catch {
                // The watchdog tries again after the next quiet period.
            } finally {
                isRefreshing = false;
                this.scheduleSyntheticDataBatchesLoad();
            }
        };
        const entry = { connection, lastMessageAt: Date.now(), refresh };
        // Closing on purpose removes the entry first, so only an unexpected close recovers.
        const isCurrent = () =>
            this.syntheticDataBatchConnections.get(batchId) === entry;

        connection.on(
            "batchCounts",
            (counts: { generatedCount: number; rejectedCount: number }) => {
                entry.lastMessageAt = Date.now();
                // Counts can arrive just after the batch finished, so only an unfinished batch takes them.
                this.updateSyntheticDataBatch(batchId, (batch) =>
                    isGenerating(batch)
                        ? { ...batch, ...counts, status: "Running" }
                        : batch,
                );
            },
        );
        connection.on(
            "completed",
            (completed: {
                status: SyntheticDataBatchStatus;
                statusReason: string | null;
                generatedCount: number;
                rejectedCount: number;
            }) => {
                entry.lastMessageAt = Date.now();
                this.updateSyntheticDataBatch(batchId, (batch) => ({
                    ...batch,
                    ...completed,
                }));
                // The update has no finish time, so reload for it.
                this.scheduleSyntheticDataBatchesLoad();
            },
        );
        connection.onreconnected(() => {
            if (isCurrent()) void refresh();
        });
        connection.onclose(() => {
            if (isCurrent()) void refresh();
        });

        this.syntheticDataBatchConnections.set(batchId, entry);
        this.startSyntheticDataBatchesWatchdog();
        void refresh();
    }

    /** Recovers any generating batch that has gone quiet, as the classifier test watchdog does. */
    private startSyntheticDataBatchesWatchdog() {
        if (this.syntheticDataBatchesWatchdog) return;
        this.syntheticDataBatchesWatchdog = setInterval(() => {
            const now = Date.now();
            for (const entry of this.syntheticDataBatchConnections.values()) {
                if (
                    now - entry.lastMessageAt <
                    LLMClassifierBuilderStore.SyntheticDataBatchSilenceMs
                )
                    continue;
                entry.lastMessageAt = now;
                void entry.refresh();
            }
        }, LLMClassifierBuilderStore.SyntheticDataBatchSilenceMs);
    }

    private closeSyntheticDataBatchConnection(batchId: string) {
        const entry = this.syntheticDataBatchConnections.get(batchId);
        this.syntheticDataBatchConnections.delete(batchId);
        void entry?.connection.stop();
        if (this.syntheticDataBatchConnections.size === 0) {
            clearInterval(this.syntheticDataBatchesWatchdog);
            this.syntheticDataBatchesWatchdog = undefined;
        }
    }

    private closeSyntheticDataBatchConnections() {
        for (const batchId of this.syntheticDataBatchConnections.keys())
            this.closeSyntheticDataBatchConnection(batchId);
    }

    /** Shows a toast once the model's last generating batch finishes. */
    private announceSyntheticDataFinished(batches: SyntheticDataBatch[]) {
        const finished = batches.filter((batch) =>
            this.syntheticDataBatchesSeenGenerating.has(batch.batchId),
        );
        this.syntheticDataBatchesSeenGenerating.clear();
        const failureHeadline = getFailureHeadline(finished);
        if (failureHeadline) {
            this.messageStore.logWarning(failureHeadline);
            return;
        }
        const written = getBatchTotals(finished).generatedCount;
        this.messageStore.logMessage(
            `Synthetic data is ready. ${written.toLocaleString()} synthetic ${
                written === 1 ? "call" : "calls"
            } written.`,
            "success",
        );
    }

    /**
     * Loads how many conversations in the Data step's test set were labeled
     * with each answer to the selected question.
     */
    public async loadDataStepLabelCounts() {
        this.dataStepLabelCountsAbortController?.abort();
        const question = this.dataLabelingQuestion;
        if (!question) {
            this.dataStepLabelCounts = undefined;
            return;
        }

        const abortController = new AbortController();
        this.dataStepLabelCountsAbortController = abortController;
        const key = this.dataStepLabelCountsRequestKey;
        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.DataStepLabelCounts,
            async () => {
                try {
                    const counts =
                        await this.testLlmClassifierService.getTestSetLabelCounts(
                            question.id,
                            this.dataStepFilters.toRequestObject(),
                            abortController.signal,
                        );
                    if (!abortController.signal.aborted)
                        runInAction(() => {
                            this.dataStepLabelCounts = counts;
                            this.dataStepLabelCountsKey = key;
                        });
                } catch (error) {
                    if (
                        error instanceof DOMException &&
                        error.name === "AbortError"
                    )
                        return;
                    runInAction(() => (this.dataStepLabelCountsErrorKey = key));
                    if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to load label counts: " + error.message,
                        );
                    }
                }
            },
        );
    }

    @action
    public setLinkedModule(module?: DataLabelingModule) {
        if (this.dataLabelingModule?.id !== module?.id)
            this.setLinkedQuestion(undefined);
        this.dataLabelingModule = module;
    }

    /** Rows for the question's answers, with the version's descriptions when its rubric is for that question. */
    @action
    private buildDataStepRubric(question?: Question) {
        // A rubric for another question stays on the version until this one gets descriptions.
        const rubric = this.classifier.implementation.rubric;
        const descriptions =
            rubric?.questionId === question?.id ? rubric?.taxonomy : undefined;
        this.dataStepRubric = [...(question?.tags ?? [])]
            .sort((a, b) => a.order - b.order)
            .map((tag) => ({
                id: tag.id,
                result: tag.value,
                definition: descriptions?.[tag.id] ?? "",
            }));
    }

    /**
     * Sets the question associated with this llm classifier. Then, populates
     * the llmResultDefinitions field based on the distinct tags on the question
     */
    @action
    public setLinkedQuestion(question?: Question) {
        if (this.dataLabelingQuestion?.id !== question?.id) {
            this.buildDataStepRubric(question);
            // Chosen numbers belong to the previous question's answers.
            this.dataStepSyntheticCallsByResult = new Map();
        }
        this.dataLabelingQuestion = question;
        this.loadAgreementLabelCounts();

        if (question) {
            this.loadLabeledConversationCounts();
        } else {
            this.labeledConversationCounts = undefined;
        }

        // Saved models and custom results keep the outputs they have.
        if (this.isEditing || !this.linkedQuestionDefinesOutputs) return;

        const builder = this.classifier.implementation.standardBuilder;
        builder.setLLMPromptTemplate(undefined);
        builder.setLLMPromptTemplateOption(undefined);
        builder.setQuestionText(question?.shortQuestionText);
        builder.setQuestionDefinition(question?.helperText);

        const tags = question?.tags;
        if (tags) {
            builder.setLLMResultDefinitions(
                this.getLLMResultDefinitionsForTags(tags),
            );
            this.classifier.setOutputs(
                tags.map((tag) => ({ id: v4(), result: tag.value })),
            );
        } else {
            builder.setLLMResultDefinitions(undefined);
        }
    }

    private getLLMResultDefinitionsForTags(tags: Tag[]) {
        const distinctTags = distinctOn(tags, (tag) => tag.value);
        return distinctTags.map((tag) => ({
            result: tag.value,
            criteria: "",
            // one empty example for ui to render
            examples: [""],
            color: sentimentToColor(tag.sentiment, tag.scoredValue),
        }));
    }

    /** Whether picking a question fills in the results, as it does for Quality and linked Insights models. */
    @computed get linkedQuestionDefinesOutputs() {
        return (
            this.classifier.implementation.llmPurpose ===
                LLMClassifierPurpose.Quality ||
            this.definedOutputsType ===
                LLMClassifierDefinedOutputsType.FromQuestion
        );
    }

    @computed get canModifyOutputs() {
        return !this.classifier.isPublished;
    }

    @action public setDefinedOutputsType(
        type: LLMClassifierDefinedOutputsType,
    ) {
        this.definedOutputsType = type;
    }

    @action
    public addModelComptueQuota() {
        this.modelComputeOption.modelComputeQuotas!.push({
            id: v4(),
            modelQuota: 0,
            modelComputeStructureMembers: [],
            quotaFrequency: ModelComputeFrequency.Weekly,
        });
    }

    @action
    public updateModelComputeQuota<K extends keyof ModelComputeQuota>(
        idx: number,
        key: K,
        value: ModelComputeQuota[K],
    ) {
        if (
            idx < 0 ||
            idx >= this.modelComputeOption.modelComputeQuotas!.length
        )
            return;
        const quotas = this.modelComputeOption.modelComputeQuotas!;
        quotas[idx][key] = value;
    }

    @action
    public removeModelComputeQuota(idx: number) {
        const quotas = this.modelComputeOption.modelComputeQuotas!;
        quotas.splice(idx, 1);
    }

    public async loadLabeledConversationCounts() {
        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.LabeledConversationCounts,
            async () => {
                try {
                    if (this.dataLabelingModule && this.dataLabelingQuestion) {
                        const counts =
                            await this.moduleService.getLabeledConversationCounts(
                                this.dataLabelingModule.id,
                                this.dataLabelingQuestion.id,
                            );
                        this.labeledConversationCounts = counts;
                    }
                } catch (error) {
                    if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to load labeled message counts: " +
                                error.message,
                        );
                    }
                }
            },
        );
    }

    /** Loads how many human labels an agreement test can use for each answer to the selected question. */
    public async loadAgreementLabelCounts() {
        this.agreementLabelCountsAbortController?.abort();
        const question = this.dataLabelingQuestion;
        if (!question) {
            this.agreementLabelCounts = undefined;
            this.agreementLabelCountsQuestionId = undefined;
            return;
        }

        const abortController = new AbortController();
        this.agreementLabelCountsAbortController = abortController;
        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.AgreementLabelCounts,
            async () => {
                try {
                    const counts =
                        await this.testLlmClassifierService.getAgreementLabelCounts(
                            question.id,
                            abortController.signal,
                        );
                    if (!abortController.signal.aborted)
                        runInAction(() => {
                            this.agreementLabelCounts = counts;
                            this.agreementLabelCountsQuestionId = question.id;
                        });
                } catch (error) {
                    if (
                        error instanceof DOMException &&
                        error.name === "AbortError"
                    )
                        return;
                    if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to load label counts: " + error.message,
                        );
                    }
                }
            },
        );
    }

    /** Pass `force` after publish or unpublish: they move the deployed version without adding one. */
    public async loadLLMClassifierVersions(force = false) {
        if (
            this.getTaskLoading(
                LLMClassifierBuilderStore.Tasks.LoadLLMClassifierVersions,
            )
        )
            return;

        // A new version is otherwise the only thing that invalidates the list, and it would be missing here.
        const { id, implementation } = this.classifier;
        const isLoaded = this.llmClassifierVersions.some(
            (version) =>
                version.classifierId === id &&
                version.versionNumber === implementation.versionNumber,
        );
        if (isLoaded && !force) return;

        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.LoadLLMClassifierVersions,
            async () => {
                try {
                    if (!this.isSaved) {
                        this.llmClassifierVersions = [];
                        return;
                    }
                    const versions =
                        await this.llmClassifierService.getLLMClassifierVersions(
                            this.classifier.id,
                        );
                    this.llmClassifierVersions = versions;
                } catch (error) {
                    if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to load LLM classifier versions: " +
                                error.message,
                        );
                    }
                }
            },
        );
    }

    public async loadApprovalReviewers() {
        if (
            this.getTaskLoading(
                LLMClassifierBuilderStore.Tasks.LoadApprovalReviewers,
            )
        )
            return;

        this.approvalReviewers = [];

        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.LoadApprovalReviewers,
            async () => {
                try {
                    this.approvalReviewers =
                        await this.llmClassifierService.getApprovalReviewers();
                } catch (error) {
                    this.messageStore.logError(
                        error instanceof Error
                            ? `Failed to load approval reviewers: ${error.message}`
                            : "Failed to load approval reviewers.",
                    );
                }
            },
        );
    }

    public async recordApprovalEvent(
        request: RecordLLMClassifierApprovalEventRequest,
    ): Promise<boolean> {
        const implementationId = this.classifier.implementation.id;
        const result = await this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.RecordApprovalEvent,
            async () => {
                try {
                    const response =
                        await this.llmClassifierService.recordApprovalEvent(
                        this.classifier.id,
                        implementationId,
                        request,
                    );
                    await this.loadLLMClassifierVersions(true);

                    const refreshed = this.llmClassifierVersions.find(
                        (version) => version.id === implementationId,
                    );
                    if (refreshed) {
                        this.classifier.implementation.approvalStatus =
                            refreshed.approvalStatus;
                    }

                    const notificationMessages =
                        approvalNotificationToastMessages(
                            request.event,
                            response.notification,
                        );
                    this.messageStore.logMessage(
                        notificationMessages.success,
                        "success",
                    );
                    if (notificationMessages.warning) {
                        this.messageStore.logWarning(
                            notificationMessages.warning,
                        );
                    }
                } catch (error) {
                    this.messageStore.logError(
                        error instanceof Error
                            ? `Failed to update approval: ${error.message}`
                            : "Failed to update approval.",
                    );
                    return AsyncTaskStatus.Error;
                }
            },
        );

        return result !== AsyncTaskStatus.Error;
    }

    public async loadChangeRecord(
        implementationId: string,
    ): Promise<LLMClassifierApprovalEventSummary[] | undefined> {
        const result = await this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.LoadChangeRecord(implementationId),
            () =>
                this.llmClassifierService.getChangeRecord(
                    this.classifier.id,
                    implementationId,
                ),
        );
        return result === AsyncTaskStatus.Error ? undefined : result;
    }

    /** The pair the comparison panel shows; unset or stale picks fall back to the previous and latest versions. */
    @computed public get comparisonVersions(): {
        before?: LLMClassifierImplementation;
        after?: LLMClassifierImplementation;
    } {
        // The endpoint returns versions newest first.
        const versions = this.llmClassifierVersions;
        const find = (versionId?: string) =>
            versions.find((version) => version.id === versionId);

        const defaults = defaultComparisonVersions(
            versions,
            this.classifier.isPublished,
            this.classifier.implementation.id,
        );

        return {
            before: find(this.comparisonBeforeVersionId) ?? defaults.before,
            after: find(this.comparisonAfterVersionId) ?? defaults.after,
        };
    }

    @action public setComparisonVersion(
        side: "before" | "after",
        versionId: string,
    ) {
        if (side === "before") this.comparisonBeforeVersionId = versionId;
        else this.comparisonAfterVersionId = versionId;
    }

    @action public clearComparisonVersions() {
        this.comparisonBeforeVersionId = undefined;
        this.comparisonAfterVersionId = undefined;
    }

    private async handleCreateClassifier(
        creatableLLMClassifier: CreateLLMClassifierRequest,
    ): Promise<LLMCLassifierResponse> {
        const llmClassifier =
            await this.llmClassifierService.createLLMClassifier(
                creatableLLMClassifier,
            );

        await this.prepareEditing(llmClassifier);

        return llmClassifier;
    }

    private async handleUpdateClassifier(
        updatableLLMClassifier: UpdateLLMClassifierRequest,
    ): Promise<LLMCLassifierResponse> {
        const classifier = await this.llmClassifierService.updateLLMClassifier(
            updatableLLMClassifier,
        );

        // A rename is saved here. Refetch before the version number changes,
        // since that reloads the chain, which names it from these variables.
        this.refreshVariables();

        await this.prepareEditing(classifier);

        return classifier;
    }

    /** The editor serialized as it stands. Savability is separate: an unsavable model must still diff. */
    private getEditorSnapshot() {
        if (
            !this.classifier.name ||
            !this.classifier.implementation ||
            !this.classifier.implementation.isComplete()
        )
            return;

        this.normalizeExecuteConditionModelReferences();
        const nameBasedExecuteConditions =
            this.createNameBasedExecuteConditions();
        const llmClassifier = this.classifier.toObject();
        llmClassifier.classifierImplementation.configuration.executeConditions =
            deepPascalCase(nameBasedExecuteConditions) as Condition[];
        this.useNameBasedModelReferencesInSerializedSteps(llmClassifier);

        llmClassifier.modelComputeOption = this.modelComputeOption;

        // Map visibility state to classifier data
        llmClassifier.isInternal =
            this.visibility === ModelVisibility.AuthenticxOnly;
        llmClassifier.rolesAccessControls =
            this.visibility === ModelVisibility.SpecificRoles
                ? this.selectedRoleIds.map((roleId) => ({
                      id: v4(),
                      roleId,
                      accessType: ClassifierAccessType.View,
                  }))
                : [];

        return llmClassifier;
    }

    /** Model prompt references that cannot be resolved to a currently available variable. */
    @computed
    private get unresolvedPromptModelReferences(): string[] {
        if (!this.classifier.implementation.isLogicChain()) return [];

        const unresolved = new Set<string>();

        for (const step of this.classifier.implementation.logicChainBuilder
            .steps) {
            for (const referenceName of getPromptVariableReferences(
                step.configuration.prompt,
            )) {
                if (!isModelVariable(referenceName)) continue;

                const isPublished = this.flatUnmappedVariables.some(
                    (candidate) =>
                        candidate.modelOutputSource &&
                        matchesLLMVariableName(candidate, referenceName),
                );
                if (!isPublished) unresolved.add(referenceName);
            }
        }

        return [...unresolved];
    }

    /** Run condition models that would save without the ClassifierId the API requires. */
    @computed
    private get unresolvedRunConditionModelReferences(): string[] {
        const unresolved = new Set<string>();

        const collect = (condition: ICondition) => {
            if (condition instanceof ConditionPrimitive) {
                if (
                    isModelVariable(condition.propertyName) &&
                    !condition.classifierId &&
                    !this.resolveModelVariable(condition.propertyName)
                ) {
                    unresolved.add(
                        condition.shortPropertyName || condition.propertyName,
                    );
                }
                return;
            }

            if (ConditionGroup.isConditionGroup(condition)) {
                condition.subRules.forEach(collect);
            }
        };

        this.classifier.implementation.configurationBuilder.executeConditions.forEach(
            collect,
        );

        return [...unresolved];
    }

    /**
     * Persists every model reference inside the step blob by name.
     *
     * ExecuteConditionsV2 carries ClassifierId separately, but PropertyName,
     * prompts, and step-action conditions remain name-based for current readers.
     *
     * TODO: Persist id-based references here after every step-configuration
     * consumer resolves model ids (prompts and nested action conditions).
     */
    private useNameBasedModelReferencesInSerializedSteps(
        llmClassifier: LLMCLassifierResponse,
    ) {
        if (
            llmClassifier.classifierImplementation.type !==
            LLMClassifierImplementationConfigurationType.LogicChaining
        )
            return;

        for (const step of llmClassifier.classifierImplementation.configuration
            .steps) {
            // Steps are Pascal-cased for the JObject wire format by the configuration builder.
            const serializedStep = step as unknown as {
                Configuration?: {
                    Prompt?: string;
                    Actions?: { Conditions?: SerializedCondition[] }[];
                };
            };
            const configuration = serializedStep.Configuration;
            if (!configuration) continue;

            for (const action of configuration.Actions ?? []) {
                action.Conditions?.forEach((condition) =>
                    this.useNameBasedModelReferenceInCondition(condition),
                );
            }
        }
    }

    /** Rewrites one serialized condition, and any it groups, to name-based. */
    private useNameBasedModelReferenceInCondition(
        condition: SerializedCondition,
    ) {
        if (condition.SubRules) {
            condition.SubRules.forEach((subRule) =>
                this.useNameBasedModelReferenceInCondition(subRule),
            );
            return;
        }

        if (!condition.PropertyName) return;

        const variable = this.resolveModelVariable(
            condition.PropertyName,
            condition.ClassifierId,
        );
        if (!variable?.modelOutputSource) return;

        // Step conditions have no V2 twin yet, so persist only the name-based
        // property understood by every current reader.
        condition.PropertyName = variable.name;
        delete condition.ClassifierId;
    }

    /** Creates the name-based compatibility conditions consumed by pre-V2 executors. */
    private createNameBasedExecuteConditions(): ICondition[] {
        const createNameBasedCondition = (
            condition: ICondition,
        ): ICondition => {
            if (ConditionPrimitive.isConditionPrimitive(condition)) {
                const variable = this.resolveModelVariable(
                    condition.propertyName,
                    condition.classifierId,
                );
                const nameBasedCondition = { ...toJS(condition) };
                nameBasedCondition.propertyName =
                    variable?.name ?? condition.propertyName;
                delete nameBasedCondition.classifierId;
                return nameBasedCondition;
            }

            if (ConditionGroup.isConditionGroup(condition)) {
                return {
                    ...toJS(condition),
                    subRules: condition.subRules.map(createNameBasedCondition),
                } as ICondition;
            }

            throw new Error("Unknown condition detected.");
        };

        return this.classifier.implementation.configurationBuilder.executeConditions.map(
            createNameBasedCondition,
        );
    }

    private resolveModelVariable(
        referenceName: string,
        classifierId?: string,
    ): SlimLLMVariable | undefined {
        const referenceMatch = this.flatUnmappedVariables.find(
            (candidate) =>
                candidate.modelOutputSource &&
                matchesLLMVariableName(candidate, referenceName),
        );
        if (referenceMatch) {
            return !classifierId ||
                referenceMatch.modelOutputSource?.classifierId === classifierId
                ? referenceMatch
                : undefined;
        }

        if (!classifierId) return undefined;

        const classifierVariables = this.flatUnmappedVariables.filter(
            (candidate) =>
                candidate.modelOutputSource?.classifierId === classifierId,
        );
        return classifierVariables.length === 1
            ? classifierVariables[0]
            : undefined;
    }

    /** Adds durable classifier identity while keeping PropertyName compatible with current readers. */
    private normalizeExecuteConditionModelReferences() {
        const conditions =
            this.classifier.implementation.configurationBuilder
                .executeConditions;

        const upgrade = (condition: ICondition) => {
            if (condition instanceof ConditionPrimitive) {
                const variable = this.resolveModelVariable(
                    condition.propertyName,
                    condition.classifierId,
                );
                if (variable?.modelOutputSource) {
                    condition.setPropertyName(variable.name);
                    condition.setClassifierId(
                        variable.modelOutputSource.classifierId,
                    );
                }
                return;
            }

            if (ConditionGroup.isConditionGroup(condition)) {
                condition.subRules.forEach(upgrade);
            }
        };

        conditions.forEach(upgrade);
    }

    private captureSaveBaseline() {
        const snapshot = this.getEditorSnapshot();
        this.savedClassifierBaseline = snapshot
            ? cloneLLMClassifierSaveSnapshot(snapshot)
            : undefined;
    }

    public getSaveChanges(
        options: { includeLongTextValues?: boolean } = {},
    ): LLMClassifierSaveChanges | undefined {
        const current = this.getEditorSnapshot();
        const roleNameById = Object.fromEntries(
            this.availableRoles.map((role) => [role.id, role.name]),
        );

        return getLLMClassifierSaveChanges(
            this.savedClassifierBaseline,
            current,
            {
                ...options,
                roleNameById,
                parentModels:
                    this.modelComputeRelationsEditor?.getParentModelNamesForDiff(),
            },
        );
    }

    /** A never-saved classifier has no baseline. Picking a purpose is what reveals the rest of the
     *  Details step, and a name is required to leave it, so either one means there's work to lose. */
    public hasUnsavedChanges(): boolean {
        const changes = this.getSaveChanges();
        if (changes) return changes.hasChanges;
        return (
            !!this.classifier.name ||
            this.classifier.implementation.llmPurpose !== undefined
        );
    }

    /**
     * Creates an llm classifier with value `this.llmClassifier` if
     * `this.isLLMClassifierSaved` is `false`. Updates the llm classifier with
     * id `this.llmClassifier.id` if `this.isLLMClassifierSaved` is `true`
     */
    public async saveLLMClassifier(versionDescription?: string) {
        const result = await this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.SaveLLMClassifier,
            async () => {
                try {
                    if (this.isLLMClassifierSavable()) {
                        const unresolved = this.unresolvedPromptModelReferences;
                        if (unresolved.length > 0) {
                            this.messageStore.logError(
                                "Prompt references a model that is no longer available: " +
                                    `${unresolved.join(
                                        ", ",
                                    )}. Reselect the output from the variable menu.`,
                            );
                            return AsyncTaskStatus.Error;
                        }

                        const unresolvedRunConditions =
                            this.unresolvedRunConditionModelReferences;
                        if (unresolvedRunConditions.length > 0) {
                            this.messageStore.logError(
                                "Run condition references a model that is no longer available: " +
                                    `${unresolvedRunConditions.join(
                                        ", ",
                                    )}. Remove that condition, then save again.`,
                            );
                            return AsyncTaskStatus.Error;
                        }

                        const savableLLMClassifier = this.getEditorSnapshot()!;
                        const parentModels =
                            (await this.modelComputeRelationsEditor?.getParentModelsForSave(
                                this.classifier.id,
                            )) ?? [];
                        const llmClassifier = {
                            ...savableLLMClassifier,
                            parentModels,
                        };
                        llmClassifier.classifierImplementation.versionDescription =
                            versionDescription;
                        // The loaded version is the one the save branches from -- with version switching
                        // that is not necessarily the published version or the newest. Set after the
                        // snapshot so it stays out of the unsaved-changes diff.
                        llmClassifier.classifierImplementation.branchedFromImplementationId =
                            llmClassifier.classifierImplementation.id;

                        const isCreating = !this.isSaved;

                        if (isCreating) {
                            await this.handleCreateClassifier(llmClassifier);
                        } else {
                            if (!versionDescription) {
                                throw new Error("version description required");
                            }

                            await this.handleUpdateClassifier(llmClassifier);
                        }

                        this.messageStore.logMessage(
                            isCreating ? "Model created" : "Model updated",
                            "success",
                        );

                        this.isSaved = true;
                        // A save adds a version without moving the deployed one, same as publish/unpublish.
                        if (!isCreating)
                            void this.loadLLMClassifierVersions(true);
                    } else {
                        this.messageStore.logError(
                            "Model missing required fields.",
                        );
                        return AsyncTaskStatus.Error;
                    }
                } catch (error) {
                    if (error instanceof ServiceError) {
                        if (await error.isValidationProblem()) {
                            const details =
                                await error.asValidationProblemDetails();

                            const errorMessageList = Object.values(
                                details.errors,
                            )
                                .flat()
                                .map((message) => `• ${message}`)
                                .join("\n");

                            this.messageStore.logError(
                                `${details.title}\n${errorMessageList}`,
                            );
                        } else {
                            this.messageStore.logError(
                                "Failed to save model: " + error.message,
                            );
                        }
                    } else if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to save model: " + error.message,
                        );
                    }

                    return AsyncTaskStatus.Error;
                }
            },
        );

        return result !== AsyncTaskStatus.Error;
    }

    @action
    public cancelTestLLMClassifier() {
        if (this.testProgress && ["Queued", "Running"].includes(this.testProgress.state)) {
            this.testProgress = { ...this.testProgress, cancellationRequested: true };
        }
        this.testLLMClassifierAbortController?.abort();
    }

    @action
    private recordTestError(error: unknown, controller: AbortController) {
        if (controller !== this.testLLMClassifierAbortController) return;
        const canceled = error instanceof DOMException && error.name === "AbortError";
        this.testProgress = {
            ...this.testProgress,
            processedCount: this.testProgress?.processedCount ?? 0,
            finishedAt: this.testProgress?.finishedAt ?? new Date().toISOString(),
            state: canceled ? "Canceled" : this.testProgress?.state === "TimedOut" ? "TimedOut" : "Failed",
            error: canceled ? undefined : error instanceof Error ? error.message : "The test could not be completed.",
        };
    }

    public async testLLMClassifierAgreement(options?: {
        isTestOne?: boolean;
        sampleSize?: number;
        testOneLabel?: string;
    }) {
        this.cancelTestLLMClassifier();
        const abortController = new AbortController();
        this.testLLMClassifierAbortController = abortController;
        this.resetTestResults();
        this.testProgress = { state: "Queued", processedCount: 0 };

        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.TestLLMClassifier,
            async () => {
                try {
                    if (!this.isLLMClassifierTestable()) return;
                    const implementation =
                        this.classifier.implementation.toObject();

                    const response =
                        await this.testLlmClassifierService.testLLMClassifierAgreement(
                            {
                                llmClassifierImplementation: implementation,
                                questionId: this.dataLabelingQuestion!.id,
                                sampleSize:
                                    options?.sampleSize ??
                                    LLMClassifierBuilderStore.MaxModelTestSampleSize,
                                llmClassifierOutputs: this.classifier.outputs,
                            },
                            options?.isTestOne ?? false,
                            options?.testOneLabel,
                            abortController.signal,
                            (progress, partial) => {
                                if (abortController.signal.aborted) return;
                                runInAction(() => {
                                    this.testProgress = progress;
                                    if (partial) this.testAgreementResponse = partial;
                                });
                            },
                        );
                    abortController.signal.throwIfAborted();
                    this.testAgreementResponse = response;
                } catch (error) {
                    this.recordTestError(error, abortController);
                    if (
                        error instanceof DOMException &&
                        error.name === "AbortError"
                    ) {
                        this.messageStore.logError("Testing cancelled!");
                    } else if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to test model: " + error.message,
                        );
                    }
                }
            },
        );
    }

    public async testLLMClassifierOnConversations(sampleSize?: number) {
        this.cancelTestLLMClassifier();
        const abortController = new AbortController();
        this.testLLMClassifierAbortController = abortController;
        this.resetTestResults();
        this.testProgress = { state: "Queued", processedCount: 0 };

        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.TestLLMClassifier,
            async () => {
                try {
                    if (!this.isLLMClassifierTestable()) return;
                    const implementation =
                        this.classifier.implementation.toObject();

                    const response =
                        await this.testLlmClassifierService.testLLMClassifierOnConversations(
                            {
                                llmClassifierImplementation: implementation,
                                filters:
                                    this.testOnConversationsFilters.toRequestObject(),
                                sampleSize,
                                llmClassifierOutputs: this.classifier.outputs,
                            },
                            abortController.signal,
                            (progress, partial) => {
                                if (abortController.signal.aborted) return;
                                runInAction(() => {
                                    this.testProgress = progress;
                                    if (partial) this.testOnConversationsResponse = partial;
                                });
                            },
                        );

                    abortController.signal.throwIfAborted();
                    this.testOnConversationsResponse = response;
                } catch (error) {
                    this.recordTestError(error, abortController);
                    if (
                        error instanceof DOMException &&
                        error.name === "AbortError"
                    ) {
                        this.messageStore.logError("Testing cancelled!");
                    } else if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to test model: " + error.message,
                        );
                    }
                }
            },
        );
    }

    public async copyLLMClassifier() {
        if (!this.isSaved) return;

        try {
            const result = await this.llmClassifiersStore.copyLLMClassifier(
                this.classifier.id,
            );
            if (result === AsyncTaskStatus.Error || !result) return;
            await this.prepareEditing(result);
        } catch (error) {
            if (error instanceof Error) {
                this.messageStore.logError(
                    "Failed to copy model: " + error.message,
                );
            }
        }
    }

    @action
    public setVisibility(v: ModelVisibility) {
        this.visibility = v;
        if (v !== ModelVisibility.SpecificRoles) {
            this.selectedRoleIds = [];
        }
        if (
            v === ModelVisibility.SpecificRoles &&
            this.availableRoles.length === 0
        ) {
            void this.loadRoles();
        }
    }

    @action
    public setSelectedRoleIds(roleIds: string[]) {
        this.selectedRoleIds = roleIds;
    }

    public async loadRoles() {
        try {
            const roles = await this.userService.getRoles();
            runInAction(() => {
                this.availableRoles = roles;
            });
        } catch (error) {
            if (error instanceof Error) {
                this.messageStore.logError(
                    "Failed to load roles: " + error.message,
                );
            }
        }
    }

    public async publishLLMClassifier(): Promise<PublishedModelResultsReconcileResult | null> {
        if (!this.isSaved) return null;
        try {
            const result = await this.llmClassifiersStore.publishLLMClassifier(
                this.classifier.id,
                this.classifier.implementation.id,
            );
            // The lower store logs server errors and resolves undefined, so the result is the only signal.
            if (result === AsyncTaskStatus.Error || !result) return null;

            this.classifier.setIsPublished(true);
            // The loaded version is the deployed one now, which moves the Published chip onto it.
            this.classifier.implementation.setIsActive(true);
            void this.loadLLMClassifierVersions(true);
            // No baseline recapture: publish sends the stored snapshot, so pending edits stay flagged.
            // Publishing is what makes a model pickable as a parent.
            this.refreshVariables();
            // setupAsyncTask can also yield an AsyncTaskStatus on error; only the DTO is an object.
            return result && typeof result === "object" ? result : null;
        } catch (error) {
            if (error instanceof Error) {
                this.messageStore.logError(
                    "Failed to publish model: " + error.message,
                );
            }
            return null;
        }
    }

    public async unpublishLLMClassifier() {
        if (!this.isSaved) return;
        try {
            const result =
                await this.llmClassifiersStore.unpublishLLMClassifier(
                    this.classifier.id,
                );
            // As with publish: a swallowed 409 must not leave the editor claiming the model is offline.
            if (result === AsyncTaskStatus.Error || !result) return;

            this.classifier.setIsPublished(false);
            // Nothing is deployed now, so this version becomes publishable again.
            this.classifier.implementation.setIsActive(false);
            void this.loadLLMClassifierVersions(true);
            // As with publish, unpublishing persists nothing from the editor, so the baseline stands.
            this.refreshVariables();
        } catch (error) {
            if (error instanceof Error) {
                this.messageStore.logError(
                    "Failed to unpublish model: " + error.message,
                );
            }
        }
    }

    /**
     * Loads a version into the editor, where saving it records a new version rather than changing it.
     */
    @action
    public async loadVersion(implementationId: string) {
        if (this.classifier.implementation.id === implementationId) return;
        try {
            const response =
                await this.llmClassifierService.getLLMClassifierVersion(
                    this.classifier.id,
                    implementationId,
                );
            await this.prepareEditing(response);
        } catch (error) {
            if (error instanceof Error) {
                this.messageStore.logError(
                    "Failed to load model version: " + error.message,
                );
            }
        }
    }

    @computed get isFromLibrary(): boolean {
        return this.classifier.createdBy === "AuthenticxClassifierLibrary";
    }

    public async updateFromLibrary(
        libraryClassifierId: string,
        versionDescription: string,
    ) {
        if (!this.isSaved) return false;

        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.UpdateFromLibrary,
            async () => {
                try {
                    await this.llmClassifierLibraryService.updateFromLibrary(
                        this.classifier.id,
                        libraryClassifierId,
                        versionDescription,
                    );

                    // Reload the classifier to get the updated state
                    const updated =
                        await this.llmClassifierService.getLLMClassifier(
                            this.classifier.id,
                        );
                    await this.prepareEditing(updated);

                    this.messageStore.logMessage(
                        "Model updated from library",
                        "success",
                    );
                    return true;
                } catch (error) {
                    if (error instanceof ServiceError) {
                        this.messageStore.logError(
                            error.errorMessage ?? error.message,
                        );
                    } else if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to update from library: " + error.message,
                        );
                    }
                    return false;
                }
            },
        );
    }

    private async loadClassifierVersionOrDefault(
        classifierId: string,
        implementationId?: string,
    ) {
        if (implementationId) {
            try {
                return await this.llmClassifierService.getLLMClassifierVersion(
                    classifierId,
                    implementationId,
                );
            } catch {
                this.messageStore.logMessage(
                    "That model version couldn't be found. Showing the current version instead.",
                    "warning",
                );
            }
        }

        return this.llmClassifierService.getLLMClassifier(classifierId);
    }

    /**
     * Opens a model in the editor. `implementationId` comes from the URL, so it may be stale or
     * belong to another model; that falls back to the default version rather than failing.
     */
    public editLLMClassifier(classifierId: string, implementationId?: string) {
        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.LoadEditLLMClassifier,
            async () => {
                try {
                    // Catches a publish or rename made outside the builder.
                    this.refreshVariables();

                    const llmClassifier =
                        await this.loadClassifierVersionOrDefault(
                            classifierId,
                            implementationId,
                        );

                    // this should never be the case. but just in cases there are bad records somewhere we handle it
                    if (!llmClassifier.classifierImplementation) {
                        this.messageStore.logError(
                            "Model has no classifier implementation and therefore cannot be edited.",
                        );
                        return;
                    }

                    await this.prepareEditing(llmClassifier);
                } catch (error) {
                    if (error instanceof Error) {
                        this.messageStore.logError(
                            "Failed to retrieve llm classifier for editing. " +
                                error.message,
                        );
                    }
                }
            },
        );
    }

    public isLLMClassifierSavable() {
        // An LLM dependency does not block saving: a save only records a draft, and the server rejects
        // the dependency at publish, which is the point it would actually reach production.
        return (
            this.classifier.implementation.configurationBuilder.isSavable() &&
            !!this.classifier.name &&
            !!this.classifier.implementation.llmModel
        );
    }

    public isLLMClassifierTestable() {
        const mustBeTrueIfLabeling =
            !!this.dataLabelingModule && !!this.dataLabelingQuestion;

        return (
            this.classifier.implementation.configurationBuilder.isTestable() &&
            !!this.classifier.implementation.llmModel &&
            (this.testingMode !== TestingMode.Agreement || mustBeTrueIfLabeling)
        );
    }

    public loadExtendedMetadataFields() {
        if (
            this.extendedMetadata.length > 0 ||
            this.getTaskLoading(
                LLMClassifierBuilderStore.Tasks.LoadExtendedMetadata,
            )
        )
            return;

        this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.LoadExtendedMetadata,
            async () => {
                this.extendedMetadata =
                    await this.classifierService.getExtendedMetadataStringClassifiers();
                this.objectExtendedMetadata =
                    await this.classifierService.getExtendedMetadataObjectClassifiers();
            },
        );
    }

    public getExtendedMetadataToUseForStep(step: LLMExecutorStep) {
        return step.configuration.useStructuredOutputs
            ? this.objectExtendedMetadata
            : this.extendedMetadata;
    }

    private loadModelComputeOptions() {
        this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.LoadModelComputeOptions,
            async () => {
                const mlModelInformation =
                    await this.modelComputeService.getModelsForLlmExecution();

                this.mlModelInformation = mlModelInformation;
            },
        );
    }

    private loadVariables() {
        return this.setupAsyncTask(
            LLMClassifierBuilderStore.Tasks.LoadVariables,
            async () => {
                const variables =
                    await this.llmClassifierService.getVariables();

                this.flatUnmappedVariables = variables;

                const coreMetadataGroup: LLMVariableGroup = {
                    label: "Metadata",
                    variables: [],
                };

                const coreAiModelGroup: LLMVariableGroup = {
                    label: "Core AI Models",
                    variables: [],
                };

                const extendedMetadataGroup: LLMVariableGroup = {
                    label: "Extended Metadata",
                    variables: [],
                };

                const llmModelGroup: LLMVariableGroup = {
                    label: LLM_MODELS_VARIABLE_GROUP_LABEL,
                    variables: [],
                };

                for (const variable of variables) {
                    let mapped: SlimLLMVariable = {
                        label: variable.label,
                        name: variable.name,
                        values: variable.values,
                        type: variable.type,
                        modelOutputSource: variable.modelOutputSource,
                        presenceOnly: variable.presenceOnly,
                    };

                    if (variable.category === LLMVariableCategory.AudioMetadata)
                        coreMetadataGroup.variables.push(mapped);
                    else if (
                        variable.category ===
                        LLMVariableCategory.ExtendedMetadata
                    )
                        extendedMetadataGroup.variables.push(mapped);
                    else if (
                        variable.category ===
                        LLMVariableCategory.MachineLearning
                    )
                        coreAiModelGroup.variables.push(mapped);
                    else if (variable.category === LLMVariableCategory.LLM)
                        llmModelGroup.variables.push(mapped);
                }

                this.variableGroups = [
                    coreMetadataGroup,
                    coreAiModelGroup,
                    llmModelGroup,
                    extendedMetadataGroup,
                ];
            },
        );
    }

    /** Names of the declared LLM parents that prevent this model from being published. */
    @computed
    public get referencedLLMModelNames(): string[] {
        return this.modelComputeRelationsEditor?.getLLMParentModelNames() ?? [];
    }

    /**
     * Why the loaded version cannot be published, or `undefined` when it can. Publishing a version
     * that reads another LLM model is not yet supported. Parents are declared per version, so this
     * can block one version of a model and not another.
     */
    @computed
    public get publishBlockedReason(): string | undefined {
        const modelNames = this.referencedLLMModelNames;
        if (modelNames.length === 0) return undefined;

        // Only publishing is blocked, whether or not a version is currently live, so the remedy is the
        // same either way.
        return `This version can't be published because it depends on the LLM ${
            modelNames.length === 1 ? "model" : "models"
        } ${modelNames
            .map((name) => `"${name}"`)
            .join(", ")}. Remove all LLM model dependencies to publish.`;
    }

    /**
     * Returns a warning message if there are variables or conditions referencing machine learning
     * models that have compute restrictions so may not run on all convos.
     */
    @computed
    public get mlModelWarningMessage(): string | undefined {
        if (this.classifier.implementation.isLogicChain()) {
            const builder = this.classifier.implementation.logicChainBuilder;
            const variables = builder.steps.flatMap((step) =>
                step.extractVariableAndPropertyNames(),
            );

            const mlOptions = this.flatUnmappedVariables.filter(
                (v) => v.category === LLMVariableCategory.MachineLearning,
            );

            const usedMlOptions = mlOptions.filter((variable) =>
                getLLMVariableNames(variable).some((name) =>
                    variables.includes(name),
                ),
            );

            const usedModelsWithComputeRestrictions =
                this.mlModelInformation.filter(
                    (i) =>
                        usedMlOptions?.find(
                            (variable) =>
                                variable.modelOutputSource?.classifierId ===
                                i.id,
                        ) && i.hasComputeRestrictions,
                );

            if (usedModelsWithComputeRestrictions.length > 0) {
                const modelNames = usedModelsWithComputeRestrictions
                    .map((i) => i.name)
                    .join(", ");
                return `This model references machine learning models (${modelNames}) that are configured to run on a subset of conversations due to quota or hierarchy restrictions. It is advised to add execute conditions to ensure model results are always present.`;
            }
        }

        return undefined;
    }

    public getResultColor(result: string): AnswerColor | undefined {
        if (
            this.dataLabelingQuestion &&
            this.dataLabelingQuestion.tags.length > 0
        ) {
            const tag = this.dataLabelingQuestion.tags.find(
                (tag) => tag.value === result,
            );
            if (!tag) return;
            return sentimentToColor(tag.sentiment, tag.scoredValue);
        }

        return AnswerColor.BLUE;
    }

    static isLLMClassifierEditable(
        llmClassifier: LLMCLassifierResponse,
    ): [false, string] | [true, undefined] {
        if (!llmClassifier.modelComputeOption) {
            return [false, "Model is missing model compute option"];
        }

        if (!llmClassifier.classifierImplementation) {
            return [false, "Model has no implementation"];
        }

        if (!llmClassifier.classifierImplementation.configuration) {
            return [false, "Model implementation has no configuration"];
        }

        switch (llmClassifier.classifierImplementation.type) {
            case LLMClassifierImplementationConfigurationType.PromptOnly:
                if (
                    !llmClassifier.classifierImplementation
                        .llmResultDefinitions ||
                    llmClassifier.classifierImplementation.llmResultDefinitions
                        .length === 0
                )
                    return [
                        false,
                        "Model implementation type is prompt only. But no result definitions exist.",
                    ];
                break;
            case LLMClassifierImplementationConfigurationType.LogicChaining:
                break;
        }

        return [true, undefined];
    }
}

function distinctOn<T, K extends keyof T>(
    values: T[],
    on: (value: T) => T[K],
): T[] {
    const distinctKeyed = new Set<T[K]>();
    const distinctObjects: T[] = [];
    values.forEach((value) => {
        const keyed = on(value);
        if (!distinctKeyed.has(keyed)) distinctObjects.push(value);
        distinctKeyed.add(keyed);
    });
    return distinctObjects;
}

interface LabeledConversationsCount {
    id: string;
    value: string;
    count: number;
}
